import * as THREE from 'three';

/**
 * class: GlobalDefs
 */
export class GlobalDefs {
    static remoteBaseUrlName: string = 'remote_base_url';

    static localBaseUrlName: string = 'local_base_url';

    static threedDirName: string = 'threed_dir';

    static itemDirName: string = 'item_dir';

    static text3DBaseDirName: string = 'tex3d_base_dir';

    static animDataDirName: string = 'animation_dir';

    static libsDirName: string = 'libs_dir';

    static clrDefPathName: string = 'clr_def_path'; // PartsDef.xml';

    static matDefPathName: string = 'mat_def_path';

    static stylDefPathName: string = 'style_def_path';

    static optiFlagsName: string = 'optiFlags';

    static msgDelim: string = '<br>';

    static lightLimit: number = 15;

    static effectiveCheckCount: number = 12;

    static NoNormalName = 'NoNormal_img';

    static GeneralShadowWall = 'shadow_wall';
}

/**
 * class: ParamNames
 */
export class ParamNames {
    static layerNumber: string = 'Layer';

    static hitPosition: string = 'hitPosition';

    static point2d: string = 'point2d';

    static rotation2d: string = 'rotation2d';
}

/**
 * class: PartType
 */
export class PartType {
    static readonly fitting = 'fit';

    static readonly furniture = 'fur';

    static readonly equipment = 'equ';

    static readonly exterior = 'ext';

    static readonly stair = 'sta';

    static readonly light = 'lig';

    static readonly setback = 'sbk';

    static readonly wireframe = 'wfm';

    static readonly decoration = 'dec';

    static readonly roof = 'roofItem';

    static readonly sash = 'sash';

    static readonly site = 'siteItem';

    static readonly wall = 'wall';

    static readonly floor = 'floor';

    static readonly ceiling = 'ceiling';
}

export type partTypes = typeof PartType[keyof typeof PartType];

/**
 * class: WebCadEven
 */
export class WebCadEventNames {
    static itemChangePickerClicked: string = 'itemChangePickerClicked';

    static deletePickerClicked: string = 'deletePickerClicked';

    static sizePickerClicked: string = 'sizePickerClicked';

    static textPickerClicked: string = 'textPickerClicked';

    static duplicatePickerClicked: string = 'duplicatePickerClicked';

    static movePickerClicked: string = 'movePickerClicked';

    static modeChange: string = 'modeChange';

    static mouseOver: string = 'mouseOver';

    static mouseOut: string = 'mouseOut';

    static mouseDown: string = 'mouseDown';

    static switcherClicked: string = 'switcherClicked';

    static wtFloorChanged: string = 'wtFloorChanged';

    static obFloorVisChanged: string = 'obFlVisChg';

    static wireframeModeChanged: string = 'wireframeModeChanged';

    static setbackModeChanged: string = 'setbackModeChanged';

    static reset3DView: string = 'reset3DView';

    static sizeObjectVisible: string = 'sizeObjectVisible';

    static initialized: string = 'initialized';

    static setup3DFinished: string = 'fin3dsetup';

    static loadingTexFinished: string = 'finTexload';

    static loadingModelFinished: string = 'fin3Dload';

    static pickerVisModeChanged: string = 'pkvis';

    static selectedObject: string = 'selectedObj';

    static unSelectedObject: string = 'unSelectedObj';

    static dragendObject: string = 'dragendObject';

    static dragObject: string = 'dragObject';
}

/**
 * class: WebCadRese
 */
export class WebCadResetOptions {
    static resetPartMaterials: string = 'resetPartMaterials';
}

/**
 * class: ColorDefs
 */
export class ColorDefs {
    static sizeColorStr = '#ffff88';

    static sizeColor = 0xffff88;

    static whiteModelOpacity = 0.6;
}

/**
 * class: LabelTexDefs
 */
export class LabelTexDefs {
    static texWid: number = 128;

    static texHgt: number = 36;

    static fontSize: number = 36;

    static baseOff: number = 4;

    static off: number = 0.02; // 寸法線からの距離.

    static wid: number = 1; // プレートの幅.

    static hgt: number = 0.25;// プレートの高さ.
}

/**
 * class: MatchDefs
 */
export class MatchDefs {
    static billBoardNames: RegExp = /autoface/;
}

/**
 * class: ShadowNames
 */
export class ShadowNames {
    static castNames: RegExp = /^(?=.*out)(?=.*(wall|base|kosi|tuma|hana|nokiura|roof|balcony))|^(?=.*rom)(?=.*(wall|kosi|ceiling))/;

    static castPartNames: RegExp = /^win|^door|^ent-|^sash-/;

    static recvPartNames: RegExp = /kaidan/;

    static recvNames: RegExp = /^(?=.*out)(?=.*(porch|site))|^(?=.*rom)(?=.*(floor|entrance|balcony))/;
}

/**
 * class: AnimNames
 */
export class AnimNames {
    static animNames: RegExp = /(^ent)|(^door)|(^toilet)|(^fusuma)|(^bath)|^(win)|^(sash)|^(shutter)/;

    static autoNames: RegExp = /(^ent)|(^door)|(^toilet)|(^fusuma)|(^bath)/;
}

/**
 * class: TweenSettings
 */
export class TweenSettings {
    // フェードイン.
    static fadeInSpeedFactor: number = 1.0;

    // 初回のカメラ移動.
    static showSpeedFactor: number = 1.2;

    // ウォークスルー前のカメラ周回スタートへの移動.
    static preMoveSpeedFactor: number = 1.5;

    // ウォークスルー前のカメラ周回.
    static aroundSpeedFactor: number = 1.5;

    // ウォークスルー前のカメラの家屋侵入.
    static inSpeedFactor: number = 7.0;

    // オービットでの各階覗き込み表示.
    static toSpeedFactor: number = 1.5;

    // カメラ回転.
    static rotCamSpeedFactor: number = 1.0;

    static twRotCamSpeedFactor: number = 5.0;

    // 落下時.
    static fallSpeedFactor: number = 2.8;

    static levitationSpeedFactor: number = 2.8;
}

/**
 * class: Limits

 */
export class Limits {
    static minFloor = 1;

    static maxFloor = 3;

    static roofNo = Limits.maxFloor + 1;

    static floorHeightMargin = 1.2; // in m.

    static topViewMargin = 20;
}

/**
 * class: WebCadCameraDefs
 */
export class WebCadCameraDefs {
    // static aspectFactor: number = 0.85;

    static containerInitOpacity: number = 0.0001;

    static orbitHeight: number = 1.4;

    static wlkthHeight: number = 1.4;

    static orbitOffDist: number = 2.5;

    static wlkthOffWall: number = 0.8;

    static wlkthTweenStartRadius: number = 22;

    static wlkthTweenStartHeight: number = 12.0;

    static turnAxis: THREE.Vector3;

    static centerPos: THREE.Vector3;

    static initShowPos: THREE.Vector3;

    static initOrbitPos: THREE.Vector3;

    static initWlkThPos: THREE.Vector3;

    static initWlkThTgt: THREE.Vector3;

    static floorViewPos: THREE.Vector3;

    static wtAnimTriggerMinDist = 0.1; // in meter.

    static wtAnimTriggerMaxDist = 3.0; // in meter.

    static wtAnimTriggerMaxDistEntryDoor = 3.0; // in meter.

    static wtAnimTriggerMaxDistInnerDoor = 1.6; // in meter.

    static wtAnimTriggerMaxDistWindow = 2.5; // in meter.

    static wtAnimSuspentionDist = 0.7; // in meter.

    static wtDefPickerMaxDist = 3.0; // in meter.

    static wtDefPickerInfDist = 100.0; // in meter.

    static wtAoriFixRatio = 0.0; // 0<=x<=1.0

    static captureCamPositions = [{ x: 0, y: 3.0, z: -10 }, { x: 10, y: 3.0, z: 0 }, { x: 0, y: 3.0, z: 10 }, { x: -10, y: 3.0, z: 0 }];
}

/**
 * class: WarpPointNames
 */
export class WarpPointNames {
    static entrance: string = 'ENTRANCE';

    static porch: string = 'PORCH';

    static position: string = 'position';

    static target: string = 'target';
}

/**
 * class: ResultDefs
 */
export class ResultDefs {
    static unsuccess = 'unsuccess';
}

/**
 * class: WebCadMessageDefs
 */
export class WebCadMessageDefs {
    static creationFailure = '<ja>住宅の建設中に<br>問題が発生しました</ja><en>Building Error!</en>';

    static miscError = '<ja>その他のエラー</ja><en>Misc error.</en>';
}

/**
 * class: ExtrasType
 */
export class ExtrasType {
    static readonly ROOF = 'ROOF';

    static readonly DEC = 'DEC';

    static readonly KABE = 'KABE';

    static readonly KOSI = 'KOSI';
    
    static readonly FALLING = 'FALLING';

    static readonly GIRDER = 'GIRDER';

    static readonly HABAKI = 'HABAKI';

    static readonly KAMACHI = 'KAMACHI';

    static readonly FLOOR = 'FLOOR';

    static readonly CEILING = 'CEILING';

    static readonly SECTION = 'SECTION';

    static readonly BASE = 'BASE';

    static readonly PORCH = 'PORCH';

    static readonly GROUND = 'GROUND';

    static readonly ROAD = 'ROAD';

    static readonly LIMIT = 'LIMIT';

    static readonly DOOR = 'DOOR';

    static readonly WIN = 'WIN';

    static readonly SHUTTER = 'SHUTTER';

    static readonly FUR = 'FUR';

    static readonly STR = 'STR';

    static readonly EQU = 'EQU';

    static readonly LIGHT = 'LIGHT';

    static readonly EXT = 'EXT';

    static readonly PIN = 'PIN';

    static readonly CAMERA_PIN = 'CAMERAPIN';
}

export type extraTypes = typeof ExtrasType[keyof typeof ExtrasType];

/**
 * class: ExtrasSubType
 */
 export class ExtrasSubType {
    static readonly ROOF = 'ROOF';

    static readonly MUNE = 'MUNE';

    static readonly HANA = 'HANA';

    static readonly URA = 'URA';

    static readonly WATERPROOF = 'WATERPROOF';

    static readonly MAKUITA = 'MAKUITA';

    static readonly OUTWALL = 'OUT-WALL';

    static readonly INNERWALL = 'INNER-WALL';
    
    static readonly FALLINGWALL = 'FALLING-WALL';

    static readonly BALCONY = 'BALCONY';

    static readonly TUMA = 'TUMA';

    static readonly HABAKI = 'HABAKI';

    static readonly KAMACHI = 'KAMACHI';

    static readonly FLOOR = 'FLOOR';

    static readonly ENTRANCE = 'ENTRANCE';

    static readonly CEILING = 'CEILING';

    static readonly GIRDER = 'GIRDER';

    static readonly SECTION = 'SECTION';

    static readonly BASE = 'BASE';

    static readonly MIZUKIRI = 'MIZUKIRI';

    static readonly PORCH = 'PORCH';

    static readonly GROUND = 'GROUND';

    static readonly ROAD = 'ROAD';

    static readonly CENTERLINE = 'CENTER-LINE';

    static readonly LIMIT = 'LIMIT';

    static readonly SASH = 'SASH';

    static readonly SHUTTER = 'SHUTTER';

    static readonly FITTING = 'FITTING';

    static readonly BATHROOM = 'BATHROOM';

    static readonly TOILET = 'TOILET';

    static readonly FUR = 'FUR';

    static readonly STR = 'STR';

    static readonly EQU = 'EQU';

    static readonly LIGHT = 'LIGHT';

    static readonly EXT = 'EXT';

    static readonly BALRAIL = 'BAL-RAIL';

    static readonly KAZARI = 'KAZARI';

    static readonly YOROI = 'YOROI';

    static readonly LATTICE = 'LATTICE';

    static readonly CORNER = 'CORNER';

    static readonly HISASI = 'HISASI';

    static readonly FBOX = 'FBOX';

    static readonly RAIL = 'RAIL';

    static readonly KASAGI = 'KASAGI';
}

export type extraSubTypes = typeof ExtrasSubType[keyof typeof ExtrasSubType];

